import { supabase } from "@/faults-app/integrations/supabase/client";
import { vfdCodes } from "@/faults-app/data/vfd-codes";
import { acCodes } from "@/faults-app/data/ac-codes";
import { homeFaults } from "@/faults-app/data/home-faults";
import { panelFaults } from "@/faults-app/data/panel-faults";
import { motorFaults } from "@/faults-app/data/motor-faults";
import { solarFaults, solarIssues } from "@/faults-app/data/solar-faults";
import { controlFaults } from "@/faults-app/data/control-faults";
import { vfdFaults } from "@/faults-app/data/vfd-general-faults";
import { transformerFaults } from "@/faults-app/data/transformer-faults";
import { atsFaults } from "@/faults-app/data/ats-faults";
import { generatorFaults } from "@/faults-app/data/generator-faults";
import { earthingFaults } from "@/faults-app/data/earthing-faults";
import { cableFaults } from "@/faults-app/data/cable-faults";
import { sensorFaults } from "@/faults-app/data/sensor-faults";
import { plcManufacturers } from "@/faults-app/data/plc-catalog";
import { plcFaults } from "@/faults-app/data/plc";
import { plcDiagnosticCodes } from "@/faults-app/data/plc/codes";
import type { PlcManufacturer, PlcSeries } from "@/faults-app/data/plc-catalog";
import type { PlcFault } from "@/faults-app/data/plc/types";
import type { PlcDiagnosticCode } from "@/faults-app/data/plc/codes/types";

export type Category = {
  id: string;
  slug: string;
  name_ar: string;
  description_ar: string | null;
  icon: string | null;
  sort_order: number;
};

export type Cause = {
  cause: string;
  probability: number;
};

export type Test = {
  test: string;
  location: string;
  method: string;
  normal: string;
  faulty: string;
};

export type Fault = {
  id: string;
  category_id: string | null;
  device_ar: string;
  name_ar: string;
  description_ar: string;
  symptoms: string[];
  causes: Cause[];
  tests: Test[];
  solutions: string[];
  severity: string;
  safety_warnings: string[];
  error_code: string | null;
  keywords: string;
  next_question_key: string | null;
};

export type DiagNode = {
  id: string;
  category_id: string | null;
  node_key: string;
  title_ar: string | null;
  is_entry: boolean;
  question_ar: string;
  explain_ar: string;
  safety_ar: string;
  yes_next: string | null;
  no_next: string | null;
  result_ar: string | null;
  result_causes: Cause[];
  result_steps: string[];
  result_solution_ar: string | null;
  severity: string | null;
  keywords: string;
};

/* =========================================================
   التصنيفات
========================================================= */

export async function fetchCategories() {
  const { data, error } = await supabase
    .from("categories")
    .select("*")
    .order("sort_order");

  if (error) throw error;

  return (data ?? []) as unknown as Category[];
}

/* =========================================================
   تحديد أقسام المكتبات المحلية
========================================================= */

async function getLocalCategoryIds() {
  try {
    const { data, error } = await supabase
      .from("categories")
      .select("id, slug, name_ar");

    if (error) {
      console.warn("تعذر تحميل التصنيفات للمكتبات المحلية:", error);
      return {
        homeCategoryId: null as string | null,
        panelCategoryId: null as string | null,
        motorCategoryId: null as string | null,
        solarCategoryId: null as string | null,
        controlCategoryId: null as string | null,
        vfdCategoryId: null as string | null,
        transformerCategoryId: null as string | null,
        atsCategoryId: null as string | null,
        generatorCategoryId: null as string | null,
        earthingCategoryId: null as string | null,
        cableCategoryId: null as string | null,
        sensorCategoryId: null as string | null,
        plcCategoryId: null as string | null,
      };
    }

    const categories =
      (data ?? []) as unknown as Pick<
        Category,
        "id" | "slug" | "name_ar"
      >[];

    const by = (
      test: (slug: string, name: string) => boolean,
    ) =>
      categories.find((c) =>
        test(
          (c.slug ?? "").toLowerCase().trim(),
          (c.name_ar ?? "").trim(),
        ),
      );

    const homeCategory = by((slug, name) =>
      name.includes("الكهرباء المنزلية") || slug.includes("home")
    );

    const panelCategory = by((slug, name) =>
      name.includes("اللوحات الكهربائية") || slug.includes("panel")
    );

    const motorCategory = by((slug, name) =>
      name.includes("المحركات الكهربائية") || slug.includes("motor")
    );

    const solarCategory = by((slug, name) =>
      name.includes("الطاقة الشمسية") ||
      slug === "pv" ||
      slug.includes("solar")
    );

    const controlCategory = by((slug, name) =>
      name.includes("دوائر السيطرة") ||
      name.includes("السيطرة") ||
      slug.includes("control")
    );

    const vfdCategory = by((slug, name) =>
      name.includes("VFD") ||
      name.includes("Inverter") ||
      slug.includes("vfd") ||
      slug.includes("drive")
    );

    const transformerCategory = by((slug, name) =>
      name.includes("المحولات") ||
      name.includes("محولات") ||
      slug.includes("transformer")
    );

    const atsCategory = by((slug, name) =>
      name.includes("ATS") ||
      name.includes("Changeover") ||
      name.includes("التحويل") ||
      slug.includes("ats") ||
      slug.includes("changeover") ||
      slug.includes("transfer")
    );

    const generatorCategory = by((slug, name) =>
      name.includes("المولدات") ||
      name.includes("مولد") ||
      slug.includes("generator") ||
      slug.includes("genset")
    );

    const earthingCategory = by((slug, name) =>
      name.includes("التأريض") ||
      name.includes("Earthing") ||
      name.includes("Grounding") ||
      slug.includes("earthing") ||
      slug.includes("ground")
    );

    const cableCategory = by((slug, name) =>
      name.includes("الكابلات") ||
      name.includes("الأسلاك") ||
      slug.includes("cable") ||
      slug.includes("wire")
    );

    const sensorCategory = by((slug, name) =>
      name.includes("الحساسات") ||
      name.includes("Sensors") ||
      name.includes("حساسات") ||
      slug.includes("sensor")
    );

    const plcCategory = by((slug, name) =>
      name === "PLC" ||
      name.includes("أعطال PLC") ||
      name.includes("اعطال PLC") ||
      slug === "plc" ||
      slug.includes("plc")
    );

    return {
      homeCategoryId: homeCategory?.id ?? null,
      panelCategoryId: panelCategory?.id ?? null,
      motorCategoryId: motorCategory?.id ?? null,
      solarCategoryId: solarCategory?.id ?? null,
      controlCategoryId: controlCategory?.id ?? null,
      vfdCategoryId: vfdCategory?.id ?? null,
      transformerCategoryId: transformerCategory?.id ?? null,
      atsCategoryId: atsCategory?.id ?? null,
      generatorCategoryId: generatorCategory?.id ?? null,
      earthingCategoryId: earthingCategory?.id ?? null,
      cableCategoryId: cableCategory?.id ?? null,
      sensorCategoryId: sensorCategory?.id ?? null,
      plcCategoryId: plcCategory?.id ?? null,
    };
  } catch (error) {
    console.warn("تعذر تحديد أقسام المكتبات المحلية:", error);
    return {
      homeCategoryId: null as string | null,
      panelCategoryId: null as string | null,
      motorCategoryId: null as string | null,
      solarCategoryId: null as string | null,
      controlCategoryId: null as string | null,
      vfdCategoryId: null as string | null,
      transformerCategoryId: null as string | null,
      atsCategoryId: null as string | null,
      generatorCategoryId: null as string | null,
      earthingCategoryId: null as string | null,
      cableCategoryId: null as string | null,
      sensorCategoryId: null as string | null,
      plcCategoryId: null as string | null,
    };
  }
}

/* =========================================================
   دمج الأعطال المحلية مع أعطال Supabase
========================================================= */

function mergeFaults(
  localFaults: Fault[],
  remoteFaults: Fault[],
): Fault[] {
  const merged = [...localFaults, ...remoteFaults];

  const unique = Array.from(
    new Map(
      merged.map((f) => [
        `${normalize(f.device_ar)}|${normalize(f.name_ar)}`,
        f,
      ]),
    ).values(),
  );

  return unique.sort((a, b) =>
    a.name_ar.localeCompare(b.name_ar, "ar"),
  );
}

/* =========================================================
   الأعطال العامة - كل المكتبات المحلية + Supabase
========================================================= */

export async function fetchFaults(categoryId?: string) {
  const ids = await getLocalCategoryIds();
  const localFaults: Fault[] = [];

  const addLocal = (
    categoryIdLocal: string | null,
    data: Array<Record<string, unknown>>,
  ) => {
    if (
      !categoryId ||
      (categoryIdLocal !== null && categoryId === categoryIdLocal)
    ) {
      localFaults.push(
        ...data.map((fault) => ({
          ...fault,
          category_id: categoryIdLocal,
        })) as Fault[],
      );
    }
  };

  addLocal(ids.homeCategoryId, homeFaults as unknown as Array<Record<string, unknown>>);
  addLocal(ids.panelCategoryId, panelFaults as unknown as Array<Record<string, unknown>>);
  addLocal(ids.motorCategoryId, motorFaults as unknown as Array<Record<string, unknown>>);
  addLocal(ids.solarCategoryId, solarFaults as unknown as Array<Record<string, unknown>>);
  addLocal(ids.controlCategoryId, controlFaults as unknown as Array<Record<string, unknown>>);
  addLocal(ids.vfdCategoryId, vfdFaults as unknown as Array<Record<string, unknown>>);
  addLocal(ids.transformerCategoryId, transformerFaults as unknown as Array<Record<string, unknown>>);
  addLocal(ids.atsCategoryId, atsFaults as unknown as Array<Record<string, unknown>>);
  addLocal(ids.generatorCategoryId, generatorFaults as unknown as Array<Record<string, unknown>>);
  addLocal(ids.earthingCategoryId, earthingFaults as unknown as Array<Record<string, unknown>>);
  addLocal(ids.cableCategoryId, cableFaults as unknown as Array<Record<string, unknown>>);
  addLocal(ids.sensorCategoryId, sensorFaults as unknown as Array<Record<string, unknown>>);

  try {
    let q = supabase.from("faults").select("*").order("name_ar");

    if (categoryId) {
      q = q.eq("category_id", categoryId);
    }

    const { data, error } = await q;

    if (error) {
      if (localFaults.length > 0) {
        console.warn(
          "تعذر تحميل أعطال Supabase، سيتم استخدام المكتبات المحلية:",
          error,
        );
        return mergeFaults(localFaults, []);
      }
      throw error;
    }

    return mergeFaults(
      localFaults,
      (data ?? []) as unknown as Fault[],
    );
  } catch (error) {
    if (localFaults.length > 0) {
      console.warn("تم استخدام مكتبات الأعطال المحلية:", error);
      return mergeFaults(localFaults, []);
    }
    throw error;
  }
}

export async function fetchFault(id: string) {
  const ids = await getLocalCategoryIds();

  const libraries: Array<{
    categoryId: string | null;
    faults: Array<Record<string, unknown>>;
  }> = [
    { categoryId: ids.homeCategoryId, faults: homeFaults as unknown as Array<Record<string, unknown>> },
    { categoryId: ids.panelCategoryId, faults: panelFaults as unknown as Array<Record<string, unknown>> },
    { categoryId: ids.motorCategoryId, faults: motorFaults as unknown as Array<Record<string, unknown>> },
    { categoryId: ids.solarCategoryId, faults: solarFaults as unknown as Array<Record<string, unknown>> },
    { categoryId: ids.controlCategoryId, faults: controlFaults as unknown as Array<Record<string, unknown>> },
    { categoryId: ids.vfdCategoryId, faults: vfdFaults as unknown as Array<Record<string, unknown>> },
    { categoryId: ids.transformerCategoryId, faults: transformerFaults as unknown as Array<Record<string, unknown>> },
    { categoryId: ids.atsCategoryId, faults: atsFaults as unknown as Array<Record<string, unknown>> },
    { categoryId: ids.generatorCategoryId, faults: generatorFaults as unknown as Array<Record<string, unknown>> },
    { categoryId: ids.earthingCategoryId, faults: earthingFaults as unknown as Array<Record<string, unknown>> },
    { categoryId: ids.cableCategoryId, faults: cableFaults as unknown as Array<Record<string, unknown>> },
    { categoryId: ids.sensorCategoryId, faults: sensorFaults as unknown as Array<Record<string, unknown>> },
  ];

  for (const library of libraries) {
    const found = library.faults.find((fault) => fault.id === id);

    if (found) {
      return {
        ...found,
        category_id: library.categoryId,
      } as Fault;
    }
  }

  const { data, error } = await supabase
    .from("faults")
    .select("*")
    .eq("id", id)
    .maybeSingle();

  if (error) throw error;

  return data as unknown as Fault | null;
}

/* =========================================================
   مكتبة PLC المحلية

   تبقى منفصلة عن fetchFaults العامة حتى ما نغيّر
   أو نحذف أي عطل من الأقسام القديمة.

   الترتيب:
   الشركة -> السلسلة -> الموديل -> الأعطال -> الأكواد
========================================================= */

export type PlcLibraryManufacturer = PlcManufacturer;
export type PlcLibrarySeries = PlcSeries;
export type PlcLibraryFault = PlcFault;
export type PlcLibraryDiagnosticCode = PlcDiagnosticCode;

export async function fetchPlcCategoryId() {
  const ids = await getLocalCategoryIds();
  return ids.plcCategoryId;
}

export function fetchPlcManufacturers() {
  return plcManufacturers;
}

export function fetchPlcManufacturer(
  manufacturerId: string,
) {
  return (
    plcManufacturers.find(
      (manufacturer) =>
        manufacturer.id === manufacturerId,
    ) ?? null
  );
}

export function fetchPlcSeries(
  manufacturerId: string,
) {
  return (
    fetchPlcManufacturer(manufacturerId)
      ?.series ?? []
  );
}

export function fetchPlcSeriesById(
  manufacturerId: string,
  seriesId: string,
) {
  return (
    fetchPlcSeries(manufacturerId).find(
      (series) => series.id === seriesId,
    ) ?? null
  );
}

export function fetchPlcModels(
  manufacturerId: string,
  seriesId: string,
) {
  const catalogSeries =
    fetchPlcSeriesById(
      manufacturerId,
      seriesId,
    );

  const catalogModels =
    catalogSeries?.models ?? [];

  const faultModels =
    plcFaults
      .filter(
        (fault) =>
          fault.manufacturer_id ===
            manufacturerId &&
          fault.series_id === seriesId,
      )
      .map((fault) => fault.model)
      .filter(Boolean);

  return Array.from(
    new Set([
      ...catalogModels,
      ...faultModels,
    ]),
  ).sort((a, b) =>
    a.localeCompare(
      b,
      "en",
      {
        numeric: true,
      },
    ),
  );
}

export function fetchPlcFaults(options?: {
  manufacturerId?: string;
  seriesId?: string;
  model?: string;
}) {
  const {
    manufacturerId,
    seriesId,
    model,
  } = options ?? {};

  return plcFaults
    .filter((fault) => {
      if (
        manufacturerId &&
        fault.manufacturer_id !==
          manufacturerId
      ) {
        return false;
      }

      if (
        seriesId &&
        fault.series_id !== seriesId
      ) {
        return false;
      }

      if (
        model &&
        fault.model !== model
      ) {
        return false;
      }

      return true;
    })
    .sort((a, b) =>
      a.title_ar.localeCompare(
        b.title_ar,
        "ar",
      ),
    );
}

export function fetchPlcDiagnosticCodes(options?: {
  manufacturerId?: string;
  seriesId?: string;
  model?: string;
}) {
  const {
    manufacturerId,
    seriesId,
    model,
  } = options ?? {};

  return plcDiagnosticCodes
    .filter((item) => {
      if (
        manufacturerId &&
        item.manufacturer_id !==
          manufacturerId
      ) {
        return false;
      }

      if (
        seriesId &&
        item.series_id !== seriesId
      ) {
        return false;
      }

      if (
        model &&
        item.model !== null &&
        item.model !== model
      ) {
        return false;
      }

      return true;
    })
    .sort((a, b) =>
      a.code.localeCompare(
        b.code,
        "en",
        {
          numeric: true,
        },
      ),
    );
}

export function fetchPlcModelBundle(
  manufacturerId: string,
  seriesId: string,
  model: string,
) {
  return {
    manufacturer:
      fetchPlcManufacturer(
        manufacturerId,
      ),
    series:
      fetchPlcSeriesById(
        manufacturerId,
        seriesId,
      ),
    model,
    faults:
      fetchPlcFaults({
        manufacturerId,
        seriesId,
        model,
      }),
    diagnosticCodes:
      fetchPlcDiagnosticCodes({
        manufacturerId,
        seriesId,
        model,
      }),
  };
}

export function searchPlcLibrary(
  query: string,
  limit = 30,
) {
  const terms = normalize(
    query.trim(),
  )
    .split(/[\s،.,؟?/_-]+/)
    .filter(Boolean);

  if (terms.length === 0) {
    return [];
  }

  const matches = (
    values: Array<
      string | null | undefined
    >,
  ) => {
    const hay = normalize(
      values
        .filter(Boolean)
        .join(" "),
    );

    return terms.every((term) =>
      hay.includes(term),
    );
  };

  const results: Array<
    | {
        kind: "manufacturer";
        manufacturer: PlcManufacturer;
      }
    | {
        kind: "series";
        manufacturer: PlcManufacturer;
        series: PlcSeries;
      }
    | {
        kind: "fault";
        fault: PlcFault;
      }
    | {
        kind: "code";
        code: PlcDiagnosticCode;
      }
  > = [];

  for (const manufacturer of plcManufacturers) {
    if (
      matches([
        manufacturer.id,
        manufacturer.name,
        ...manufacturer.aliases,
      ])
    ) {
      results.push({
        kind: "manufacturer",
        manufacturer,
      });
    }

    for (const series of manufacturer.series) {
      if (
        matches([
          manufacturer.name,
          series.id,
          series.name,
          ...series.aliases,
          ...series.engineeringSoftware,
          ...(series.models ?? []),
        ])
      ) {
        results.push({
          kind: "series",
          manufacturer,
          series,
        });
      }
    }
  }

  for (const fault of plcFaults) {
    if (
      matches([
        fault.manufacturer_id,
        fault.series_id,
        fault.model,
        fault.code ?? "",
        fault.title_ar,
        fault.description_ar,
        ...fault.symptoms,
        ...fault.causes.map(
          (cause) => cause.cause,
        ),
        ...fault.solutions,
        ...fault.keywords,
      ])
    ) {
      results.push({
        kind: "fault",
        fault,
      });
    }
  }

  for (const item of plcDiagnosticCodes) {
    if (
      matches([
        item.manufacturer_id,
        item.series_id,
        item.model ?? "",
        item.code,
        item.code_type,
        item.title_ar,
        item.description_ar,
        ...item.symptoms,
        ...item.causes,
        ...item.solutions,
        ...item.keywords,
      ])
    ) {
      results.push({
        kind: "code",
        code: item,
      });
    }
  }

  return results.slice(0, limit);
}

/* =========================================================
   التشخيص التفاعلي
========================================================= */

export async function fetchEntryNodes() {
  const { data, error } = await supabase
    .from("diagnostic_nodes")
    .select("*")
    .eq("is_entry", true);

  if (error) throw error;

  return (data ?? []) as unknown as DiagNode[];
}

export async function fetchNode(key: string) {
  const { data, error } = await supabase
    .from("diagnostic_nodes")
    .select("*")
    .eq("node_key", key)
    .maybeSingle();

  if (error) throw error;

  return data as unknown as DiagNode | null;
}

/* =========================================================
   درجة الخطورة
========================================================= */

export const severityTone = (s?: string | null) =>
  s === "حرجة"
    ? "bg-destructive/15 text-destructive"
    : s === "عالية"
      ? "bg-warning/15 text-warning"
      : s === "متوسطة"
        ? "bg-accent text-accent-foreground"
        : "bg-muted text-muted-foreground";

/* =========================================================
   البحث داخل الأعطال العامة
========================================================= */

export function scoreFault(
  f: Fault,
  terms: string[],
) {
  const hay = [
    f.name_ar,
    f.device_ar,
    f.description_ar,
    f.keywords,
    f.error_code ?? "",
    ...f.symptoms,
    ...f.causes.map((c) => c.cause),
    ...f.solutions,
  ]
    .join(" ")
    .toLowerCase();

  return terms.reduce(
    (acc, t) =>
      acc + (hay.includes(t) ? 1 : 0),
    0,
  );
}

/* =========================================================
   توحيد النص العربي للبحث
========================================================= */

export function normalize(text: string) {
  return text
    .replace(/[إأآا]/g, "ا")
    .replace(/ى/g, "ي")
    .replace(/ة/g, "ه")
    .replace(/[\u064B-\u0652]/g, "")
    .toLowerCase();
}

export function searchTerms(query: string) {
  return normalize(query)
    .split(/[\s،.,؟?]+/)
    .filter((w) => w.length > 2);
}

/* =========================================================
   رموز أعطال الأجهزة
========================================================= */

export type CodeDomain =
  | "vfd"
  | "ac"
  | "solar"
  | "inverter";

export const DOMAIN_LABEL: Record<
  CodeDomain,
  string
> = {
  vfd: "VFD / درايف",
  ac: "مكيفات وسبلت",
  solar: "إنفرتر طاقة شمسية",
  inverter: "إنفرتر",
};

export const DOMAIN_ROUTE: Record<
  CodeDomain,
  string
> = {
  vfd: "/vfd-codes",
  ac: "/ac-codes",
  solar: "/solar-codes",
  inverter: "/inverter-codes",
};

export type EquipmentCode = {
  id: string;
  domain: CodeDomain;
  brand: string;
  model: string;
  code: string;
  meaning_ar: string;
  cause_ar: string;
  steps: string[];
  solutions: string[];
  parts: string[];
  safety_ar: string;
  keywords: string;
};

/* =========================================================
   اختيار المكتبة المحلية حسب القسم
========================================================= */

function getLocalCodes(
  domain: CodeDomain,
): EquipmentCode[] {
  switch (domain) {
    case "vfd":
      return vfdCodes;

    case "ac":
      return acCodes;

    case "solar":
      return [];

    case "inverter":
      return [];

    default:
      return [];
  }
}

/* =========================================================
   إزالة التكرار وترتيب الأكواد
========================================================= */

function mergeEquipmentCodes(
  localCodes: EquipmentCode[],
  remoteCodes: EquipmentCode[],
) {
  /*
    نخلي الأكواد المحلية أولاً.
    إذا موجود نفس:
    domain + brand + model + code
    مرتين، يبقى سجل واحد فقط.
  */

  const merged = [
    ...localCodes,
    ...remoteCodes,
  ];

  const unique = Array.from(
    new Map(
      merged.map((c) => [
        `${normalize(c.domain)}|${normalize(c.brand)}|${normalize(
          c.model,
        )}|${normalize(c.code)}`,
        c,
      ]),
    ).values(),
  );

  return unique.sort((a, b) => {
    const brandCompare = a.brand.localeCompare(
      b.brand,
      "en",
    );

    if (brandCompare !== 0) {
      return brandCompare;
    }

    const modelCompare = a.model.localeCompare(
      b.model,
      "en",
    );

    if (modelCompare !== 0) {
      return modelCompare;
    }

    return a.code.localeCompare(
      b.code,
      "en",
      {
        numeric: true,
      },
    );
  });
}

/* =========================================================
   جلب رموز قسم معين

   يجمع بين:
   - المكتبة المحلية VFD
   - المكتبة المحلية AC
   - الأكواد الموجودة أصلاً في Supabase

   بدون حذف البيانات القديمة.
========================================================= */

export async function fetchEquipmentCodes(
  domain: CodeDomain,
) {
  const localCodes = getLocalCodes(domain);

  try {
    const { data, error } = await supabase
      .from("equipment_codes")
      .select("*")
      .eq("domain", domain)
      .order("brand")
      .order("model")
      .order("code");

    if (error) {
      /*
        إذا كانت لدينا مكتبة محلية،
        نستمر بالعمل حتى لو Supabase لم يستجب.
      */
      if (localCodes.length > 0) {
        console.warn(
          `تعذر تحميل equipment_codes من Supabase للقسم ${domain}:`,
          error,
        );

        return mergeEquipmentCodes(
          localCodes,
          [],
        );
      }

      throw error;
    }

    const remoteCodes =
      (data ?? []) as unknown as EquipmentCode[];

    return mergeEquipmentCodes(
      localCodes,
      remoteCodes,
    );
  } catch (error) {
    if (localCodes.length > 0) {
      console.warn(
        `تم استخدام المكتبة المحلية للقسم ${domain}:`,
        error,
      );

      return mergeEquipmentCodes(
        localCodes,
        [],
      );
    }

    throw error;
  }
}

/* =========================================================
   جلب جميع رموز الأعطال

   يستخدم بالبحث الموحد في الموقع.
========================================================= */

export async function fetchAllEquipmentCodes() {
  const allLocalCodes: EquipmentCode[] = [
    ...vfdCodes,
    ...acCodes,
  ];

  try {
    const { data, error } = await supabase
      .from("equipment_codes")
      .select("*")
      .order("brand")
      .order("model")
      .order("code");

    if (error) {
      console.warn(
        "تعذر تحميل رموز Supabase، سيتم استخدام المكتبات المحلية:",
        error,
      );

      return mergeEquipmentCodes(
        allLocalCodes,
        [],
      );
    }

    const remoteCodes =
      (data ?? []) as unknown as EquipmentCode[];

    return mergeEquipmentCodes(
      allLocalCodes,
      remoteCodes,
    );
  } catch (error) {
    console.warn(
      "تم استخدام المكتبات المحلية:",
      error,
    );

    return mergeEquipmentCodes(
      allLocalCodes,
      [],
    );
  }
}

/* =========================================================
   البحث داخل جميع رموز الأعطال
========================================================= */

export function searchCodes(
  codes: EquipmentCode[],
  query: string,
  limit = 8,
) {
  const terms = normalize(query.trim())
    .split(/[\s،.,؟?]+/)
    .filter(Boolean);

  if (terms.length === 0) {
    return [];
  }

  return codes
    .map((c) => {
      const hay = normalize(
        [
          c.brand,
          c.model,
          c.code,
          c.meaning_ar,
          c.cause_ar,
          c.keywords,
          DOMAIN_LABEL[c.domain],
          ...c.steps,
          ...c.solutions,
          ...c.parts,
        ].join(" "),
      );

      const score = terms.reduce(
        (acc, term) =>
          acc +
          (hay.includes(term) ? 1 : 0),
        0,
      );

      /*
        نعطي أفضلية قوية إذا كان رمز العطل
        مطابقاً تماماً لما كتبه المستخدم.
      */

      const exactCode =
        normalize(c.code) ===
        normalize(query.trim());

      const codeBonus = exactCode ? 5 : 0;

      return {
        c,
        score: score + codeBonus,
      };
    })
    .filter((result) => result.score > 0)
    .sort(
      (a, b) =>
        b.score - a.score ||
        a.c.brand.localeCompare(
          b.c.brand,
          "en",
        ),
    )
    .slice(0, limit);
}

/* =========================================================
   أعطال أنظمة الطاقة الشمسية
========================================================= */

export type SolarIssue = {
  id: string;
  title_ar: string;
  description_ar: string;
  causes: Cause[];
  steps: string[];
  measurements: string[];
  solution_ar: string;
  severity: string;
  safety_ar: string;
  keywords: string;
  sort_order: number;
};

export async function fetchSolarIssues() {
  try {
    const { data, error } = await supabase
      .from("solar_issues")
      .select("*")
      .order("sort_order");

    if (error) {
      console.warn(
        "تعذر تحميل أعطال الطاقة الشمسية من Supabase، سيتم استخدام المكتبة المحلية:",
        error,
      );

      return solarIssues as unknown as SolarIssue[];
    }

    const remoteIssues =
      (data ?? []) as unknown as SolarIssue[];

    const merged = [
      ...(solarIssues as unknown as SolarIssue[]),
      ...remoteIssues,
    ];

    return Array.from(
      new Map(
        merged.map((issue) => [
          normalize(issue.title_ar),
          issue,
        ]),
      ).values(),
    ).sort(
      (a, b) =>
        a.sort_order - b.sort_order ||
        a.title_ar.localeCompare(
          b.title_ar,
          "ar",
        ),
    );
  } catch (error) {
    console.warn(
      "تم استخدام مكتبة الطاقة الشمسية المحلية:",
      error,
    );

    return solarIssues as unknown as SolarIssue[];
  }
}

